import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faCalendarDays,
  faCheckCircle,
  faCircleHalfStroke,
  faClockRotateLeft,
  faHeadset,
  faHourglassHalf,
  faLayerGroup,
  faPlus,
  faSpinner,
  faTicket,
  faTriangleExclamation,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import { getById } from "../../contexts/apicontexts";
import { fetchCustomerTicketAnalytics } from "../Analytics_Reporting_Module_Tickets/customerTicketAnalyticsService";
import config from "../../config";

const BRAND = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const STATUS_COLORS = {
  "new":         "#6366f1",
  "in progress": "#3b82f6",
  "on hold":     "#f59e0b",
  "resolved":    "#10b981",
  "closed":      "#64748b",
};

const colorForStatus = (s) => STATUS_COLORS[String(s ?? "").toLowerCase()] ?? "#94a3b8";

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "2-digit" }).format(new Date(v));
  } catch { return String(v); }
};

const getAgreementDays = (endDate) => {
  if (!endDate) return null;
  const diffDays = Math.ceil((new Date(endDate).setHours(0,0,0,0) - new Date().setHours(0,0,0,0)) / 86400000);
  return diffDays;
};

const safeParseJson = (v) => { try { return JSON.parse(v); } catch { return null; } };

// ── Small reusable pieces ─────────────────────────────────────────────────────

const KpiCard = ({ icon, label, value, sub, accent, to }) => {
  const inner = (
    <div className="flex items-start gap-4 p-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-sm" style={{ background: accent ?? BRAND }}>
        <FontAwesomeIcon icon={icon} className="text-base" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-0.5 text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{value}</div>
        {sub ? <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{sub}</div> : null}
      </div>
    </div>
  );
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition dark:border-gray-700 dark:bg-[#161B22]">
      {to ? <Link to={to} className="block hover:bg-gray-50 dark:hover:bg-gray-800/40 transition">{inner}</Link> : inner}
      <div className="h-0.5 w-full" style={{ background: accent ?? BRAND }} />
    </div>
  );
};

const ChartCard = ({ title, icon, children, className = "" }) => (
  <div className={`overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22] ${className}`}>
    <div className="flex items-center gap-2.5 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white text-xs" style={{ background: BRAND }}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <h3 className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const c = colorForStatus(status);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
      style={{ borderColor: `${c}55`, background: `${c}18`, color: c }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
      {status}
    </span>
  );
};

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: p } = payload[0];
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-xs shadow-xl">
      <div className="flex items-center gap-2 font-bold text-white">
        <span className="h-2 w-2 rounded-full" style={{ background: p.fill }} />
        {name}
      </div>
      <div className="mt-1 text-gray-300">{value} tickets · {p.percent}%</div>
    </div>
  );
};

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-xs shadow-xl">
      <div className="mb-1 font-bold text-white">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-gray-300">
          <span className="h-2 w-2 rounded-full" style={{ background: p.fill ?? p.color }} />
          {p.name}: <span className="font-semibold text-white">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

export default function CustomerDashboard({ displayName }) {
  const user = useMemo(() => safeParseJson(localStorage.getItem("user")), []);
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const year = new Date().getFullYear();
        const [dashRes, analyticsRes] = await Promise.allSettled([
          getById(`${config.baseurl}Customer/dashboard`),
          fetchCustomerTicketAnalytics({ mode: "yearly", year: String(year) }),
        ]);

        if (cancelled) return;

        if (dashRes.status === "fulfilled" && dashRes.value?.status === 200) {
          setDashboard(dashRes.value.data ?? null);
        }
        if (analyticsRes.status === "fulfilled") {
          setAnalytics(analyticsRes.value);
        }
      } catch {
        if (!cancelled) setError("Dashboard data could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const agreementDays = useMemo(() => getAgreementDays(dashboard?.agreementEndDate), [dashboard?.agreementEndDate]);

  const openTickets = useMemo(() =>
    (dashboard?.newTickets ?? 0) + (dashboard?.inProgressTickets ?? 0) + (dashboard?.onHoldTickets ?? 0),
  [dashboard]);

  const statusPieData = useMemo(() => {
    if (!analytics?.statusBreakdown?.length) return [];
    return analytics.statusBreakdown.map((s) => ({
      name: s.label,
      value: s.count,
      percent: Math.round(s.percent),
      fill: colorForStatus(s.label),
    }));
  }, [analytics]);

  const trendData = useMemo(() => analytics?.trend?.slice(-12) ?? [], [analytics]);

  const agreementLabel = useMemo(() => {
    if (agreementDays === null) return null;
    if (agreementDays < 0) return { text: "Agreement expired", color: "#ef4444" };
    if (agreementDays === 0) return { text: "Expires today", color: "#f59e0b" };
    if (agreementDays <= 30) return { text: `${agreementDays}d left`, color: "#f59e0b" };
    return { text: `${agreementDays}d left`, color: "#10b981" };
  }, [agreementDays]);

  if (loading) return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" style={{ color: "#C04C2A" }} />
        <span className="text-sm font-medium">Loading your dashboard…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 dark:border-rose-800 dark:bg-rose-900/20">
      <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400">
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <span className="text-sm font-semibold">{error}</span>
      </div>
    </div>
  );

  const name = dashboard?.name || user?.fullName || displayName || "Customer";

  return (
    <div className="space-y-5">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-2xl text-white shadow-lg" style={{ background: BRAND }}>
        <div className="pointer-events-none absolute -right-14 -top-14 h-52 w-52 rounded-full bg-white/5 blur-3xl" />
        <div className="pointer-events-none absolute -left-8 bottom-0 h-32 w-32 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.12)" }} />
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 60%, transparent)" }} />

        <div className="relative flex flex-col gap-4 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-100">
              <FontAwesomeIcon icon={faHeadset} className="text-[9px]" />
              Customer Portal
            </div>
            <h1 className="mt-2 text-xl font-extrabold tracking-tight drop-shadow-sm sm:text-2xl">
              Welcome, {name}
            </h1>
            <p className="mt-0.5 text-sm text-orange-100">
              {dashboard?.organizationName ? `${dashboard.organizationName} · ` : ""}
              {dashboard?.email || user?.email || ""}
            </p>
            {agreementLabel ? (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-orange-100">
                <FontAwesomeIcon icon={faCalendarDays} className="text-[10px]" />
                {agreementLabel.text}
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            <Link
              to="/tickets"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-xs font-bold shadow-sm transition hover:bg-orange-50"
              style={{ color: "#C04C2A" }}
            >
              <FontAwesomeIcon icon={faTicket} />
              My Tickets
            </Link>
            <Link
              to="/tickets"
              className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/15 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/25"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Ticket
            </Link>
          </div>
        </div>

        {/* Status mini-strip */}
        {dashboard ? (
          <div className="relative grid grid-cols-2 gap-px border-t border-white/10 bg-white/10 sm:grid-cols-4">
            {[
              { label: "Total",       value: dashboard.totalTickets ?? 0,    color: "#fff" },
              { label: "Open",        value: openTickets,                     color: "#fbbf24" },
              { label: "Resolved",    value: dashboard.resolvedTickets ?? 0,  color: "#34d399" },
              { label: "Active Users",value: dashboard.activeCustomerUserCount ?? 0, color: "#a78bfa" },
            ].map((item) => (
              <div key={item.label} className="bg-white/5 px-4 py-3 text-center">
                <div className="text-xl font-extrabold" style={{ color: item.color }}>{item.value}</div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-orange-100">{item.label}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          icon={faLayerGroup}
          label="Total Tickets"
          value={dashboard?.totalTickets ?? 0}
          sub="All time"
          accent="linear-gradient(135deg,#C04C2A,#4A1B13)"
          to="/tickets"
        />
        <KpiCard
          icon={faHourglassHalf}
          label="Open Tickets"
          value={openTickets}
          sub="New + In Progress + On Hold"
          accent="linear-gradient(135deg,#f59e0b,#d97706)"
          to="/tickets"
        />
        <KpiCard
          icon={faCheckCircle}
          label="Resolved"
          value={dashboard?.resolvedTickets ?? 0}
          sub="Completed by support"
          accent="linear-gradient(135deg,#10b981,#059669)"
        />
        <KpiCard
          icon={faUsers}
          label="Active Users"
          value={dashboard?.activeCustomerUserCount ?? 0}
          sub={dashboard?.maxActiveUsers ? `of ${dashboard.maxActiveUsers} max` : "Customer portal users"}
          accent="linear-gradient(135deg,#6366f1,#4f46e5)"
        />
      </div>

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

        {/* Status pie */}
        <ChartCard title="Ticket Status Breakdown" icon={faCircleHalfStroke}>
          {statusPieData.length ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3}>
                    {statusPieData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex flex-col gap-1.5">
                {statusPieData.map((s) => (
                  <div key={s.name} className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: s.fill }} />
                      <span className="font-medium text-slate-700 dark:text-slate-300">{s.name}</span>
                    </div>
                    <span className="font-bold text-slate-900 dark:text-white">{s.value} <span className="font-normal text-slate-500 dark:text-slate-400">({s.percent}%)</span></span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex h-40 items-center justify-center text-xs text-slate-400 dark:text-slate-500">No data yet</div>
          )}
        </ChartCard>

        {/* Ticket trend */}
        <ChartCard title="Ticket Trend" icon={faArrowTrendUp} className="lg:col-span-2">
          {trendData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
                <Tooltip content={<CustomBarTooltip />} />
                <Line type="monotone" dataKey="total"    name="Total"    stroke="#C04C2A" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="pending"  name="Pending"  stroke="#f59e0b" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[220px] items-center justify-center text-xs text-slate-400 dark:text-slate-500">No trend data yet</div>
          )}
        </ChartCard>
      </div>

      {/* ── Status bar chart + info ── */}
      {statusPieData.length ? (
        <ChartCard title="Tickets by Status" icon={faTicket}>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={statusPieData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9ca3af" }} />
              <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
              <Tooltip content={<CustomBarTooltip />} />
              <Bar dataKey="value" name="Tickets" radius={[6, 6, 0, 0]}>
                {statusPieData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : null}

      {/* ── Info cards ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

        {/* Customer profile */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22]">
          <div className="flex items-center gap-2.5 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white text-xs" style={{ background: BRAND }}>
              <FontAwesomeIcon icon={faUsers} />
            </span>
            <h3 className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">Account Info</h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {[
              { label: "Customer Code",   value: dashboard?.customerCode || "—" },
              { label: "Organization",    value: dashboard?.organizationName || "—" },
              { label: "Email",           value: dashboard?.email || "—" },
              { label: "Mobile",          value: dashboard?.mobile || "—" },
              { label: "Domain",          value: dashboard?.domain || "—" },
              { label: "Status",          value: dashboard?.isCurrentCustomer ? "Active" : "Inactive" },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-3 px-5 py-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{row.label}</span>
                <span className="text-sm font-semibold text-slate-900 dark:text-white truncate max-w-[55%] text-right">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Agreement + ticket stats */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22]">
          <div className="flex items-center gap-2.5 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white text-xs" style={{ background: BRAND }}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>
            <h3 className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">Agreement &amp; Support Summary</h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {[
              { label: "Agreement Start", value: formatDate(dashboard?.agreementStartDate) },
              { label: "Agreement End",   value: formatDate(dashboard?.agreementEndDate) },
              { label: "Last Ticket",     value: formatDate(dashboard?.lastTicketAt) },
              { label: "New Tickets",     value: dashboard?.newTickets ?? 0 },
              { label: "In Progress",     value: dashboard?.inProgressTickets ?? 0 },
              { label: "On Hold",         value: dashboard?.onHoldTickets ?? 0 },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-3 px-5 py-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{row.label}</span>
                <span className="text-sm font-semibold text-slate-900 dark:text-white">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Resolution rate bar ── */}
      {analytics?.summary ? (
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#161B22]">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="text-sm font-bold text-slate-800 dark:text-white">Resolution Rate</div>
            <div className="text-lg font-extrabold" style={{ color: "#C04C2A" }}>
              {Math.round(analytics.summary.resolutionRate ?? 0)}%
            </div>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, Math.round(analytics.summary.resolutionRate ?? 0))}%`, background: BRAND }}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-400">
            <span>{analytics.summary.totalTickets} total tickets</span>
            <span>{analytics.summary.resolvedTickets} resolved</span>
            <span>{analytics.summary.pendingTickets} pending</span>
          </div>
        </div>
      ) : null}

    </div>
  );
}
